import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import {
  ManagerProgressionTaskSlug,
  PackConfigGroup,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { BottomPortal } from '@sorare/core/src/atoms/layout/BottomPortal';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { PullToRefetch } from '@sorare/core/src/atoms/navigation/PullToRefetch';
import { EssenceBalance } from '@sorare/core/src/components/balance/EssenceBalance';
import { ScarcityBackground } from '@sorare/core/src/components/card/ScarcityBackground';
import { GemsBalance } from '@sorare/core/src/components/commonGame/GemsBalance';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSeriesNav } from '@sorare/core/src/hooks/commonGame/useSeriesNav';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { groupBy } from '@sorare/core/src/lib/arrays';
import { getCommonCardShardsCount } from '@sorare/core/src/lib/currentUser';
import { isTypeFn } from '@sorare/core/src/lib/gql';
import {
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { DailyDeals } from 'components/commonCard/BuyPacks/DailyDeals';
import { SeriesDailyClaimModule } from 'components/commonCard/BuyPacks/SeriesDailyClaimModule';

import { BuyGemsBlock } from '../BuyGemsBlock';
import type {
  CommonPacksPage_marketRoot,
  CommonPacksPage_tokenRoot,
} from './__generated__/index.graphql';
import { CardPackGroup } from '../BuyPacks/CardPackGroup';
import { CommonPacksHeader } from '../BuyPacks/CommonPacksHeader';
import { DailyDealsTitle } from '../BuyPacks/DailyDeals/DailyDealsTitle';
import { DailyDealsBlock } from '../BuyPacks/DailyDealsBlock';
import { SeriesCardPackGroup } from '../BuyPacks/SeriesCardPackGroup';
import { usePackAssets } from '../PacksTypePage/usePackAssets';

const Root = styled.div`
  position: relative;
  isolation: isolate;
  container-type: inline-size;
  padding-bottom: calc(var(--unit) * 8);
`;

const Positioned = styled.div`
  position: sticky;
  left: 0;
  right: 0;
  top: var(--current-stack-height);
  height: 0;
  width: 100%;
  z-index: -1;
`;

const BackgroundWrapper = styled.div`
  position: absolute;
  inset: 0;
  isolation: isolate;
  height: calc(var(--100vh) - var(--current-stack-height));
`;

const Balance = styled(Horizontal)`
  padding: var(--double-unit);
  @media ${laptopAndAbove} {
    padding: var(--triple-unit);
  }
`;

const Wrapper = styled(Vertical)`
  gap: var(--double-unit);
  @container ${laptopAndAbove} {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--triple-unit);
  }
`;

const SeriesDailyClaimWrapper = styled(Horizontal)`
  justify-content: flex-end;
  width: calc(100 * var(--vw) - var(--quadruple-unit));
  @media ${tabletAndAbove} {
    display: none;
  }
`;

const FirstColumnItem = styled.div`
  @container ${laptopAndAbove} {
    grid-column: 1;
  }
`;

type Props = {
  refetch: () => Promise<unknown>;
  marketRoot: CommonPacksPage_marketRoot;
  tokenRoot?: CommonPacksPage_tokenRoot;
};
export const CommonPacksPage = ({ refetch, marketRoot, tokenRoot }: Props) => {
  const {
    flags: { usePurchasableItemId = false, useEnableSeriesMarket = false },
  } = useFeatureFlags();

  const isSimplifiedNav = useSeriesNav();
  const gemsPacks =
    (usePurchasableItemId
      ? tokenRoot?.purchasableItems
      : marketRoot?.gemsPacks) || [];

  const { currentUser } = useCurrentUserContext();
  const openDialog = useOpenDialog();
  const { dailyDealCardPackGroups, marketCardPackGroups } = groupBy(
    marketRoot.cardPackGroups,
    cardPackGroup => {
      if (cardPackGroup.packConfigGroupType === PackConfigGroup.DAILY_DEAL) {
        return 'dailyDealCardPackGroups';
      }
      return 'marketCardPackGroups';
    }
  );
  const dailyDealCardPackGroup = dailyDealCardPackGroups?.at(0);

  const { bgImage } = usePackAssets({
    cardPackGroupId: dailyDealCardPackGroup?.id || '',
  });

  const onPackSelect = (_: string, cardPackSlug: string) => {
    openDialog(DialogKey.cardPack, cardPackSlug);
  };

  const dailyClaimTask = marketRoot.tasks
    .filter(isTypeFn('Task'))
    .find(task => task.name === ManagerProgressionTaskSlug.DAILY_CLAIM);

  return (
    <Root>
      <PullToRefetch refetch={refetch}>
        {!!useEnableSeriesMarket && (
          <Positioned>
            <BackgroundWrapper>
              <ScarcityBackground
                rarity={Rarity.common}
                editionName={'winter'}
              />
            </BackgroundWrapper>
          </Positioned>
        )}
        <Vertical gap={3}>
          {!useEnableSeriesMarket && (
            <CommonPacksHeader
              marketRoot={marketRoot}
              refetch={refetch}
              backgroundUrl={bgImage}
              title={<DailyDealsTitle cardPackGroup={dailyDealCardPackGroup} />}
              balances={
                <Horizontal>
                  <EssenceBalance
                    value={
                      getCommonCardShardsCount(
                        currentUser,
                        'common-base-set'
                      ) || 0
                    }
                    rarity={Rarity.common}
                    cardSetSlug="common-base-set"
                  />
                  <GemsBalance
                    balance={currentUser?.gemsBalances[0]?.amount || 0}
                  />
                </Horizontal>
              }
            >
              {!!dailyDealCardPackGroup && (
                <DailyDeals
                  cardPackGroup={dailyDealCardPackGroup}
                  onClickBuy={onPackSelect}
                />
              )}
            </CommonPacksHeader>
          )}
          {!!useEnableSeriesMarket && (
            <Balance>
              <EssenceBalance
                value={getCommonCardShardsCount(currentUser, 'winter') || 0}
                rarity={Rarity.common}
                cardSetSlug="winter"
              />
              <GemsBalance
                balance={currentUser?.gemsBalances[0]?.amount || 0}
              />
            </Balance>
          )}
          <Container maxWidth={1320}>
            {useEnableSeriesMarket ? (
              <Wrapper gap={3}>
                {marketCardPackGroups?.map(group => (
                  <SeriesCardPackGroup
                    key={group.id}
                    cardPackGroup={group}
                    onClickBuy={onPackSelect}
                  />
                ))}

                <FirstColumnItem>
                  <BuyGemsBlock gemsPacks={gemsPacks} />
                </FirstColumnItem>
                {!!dailyDealCardPackGroup && !isSimplifiedNav && (
                  <DailyDealsBlock
                    bgImage={bgImage}
                    title={
                      <DailyDealsTitle cardPackGroup={dailyDealCardPackGroup} />
                    }
                  >
                    <DailyDeals
                      cardPackGroup={dailyDealCardPackGroup}
                      onClickBuy={onPackSelect}
                    />
                  </DailyDealsBlock>
                )}
              </Wrapper>
            ) : (
              <Vertical gap={3}>
                {marketCardPackGroups?.map(group => (
                  <CardPackGroup
                    key={group.id}
                    cardPackGroup={group}
                    onClickBuy={onPackSelect}
                  />
                ))}
              </Vertical>
            )}
          </Container>
        </Vertical>
      </PullToRefetch>
      {!!useEnableSeriesMarket && (
        <BottomPortal.Above>
          <SeriesDailyClaimWrapper>
            {!!dailyClaimTask && (
              <SeriesDailyClaimModule task={dailyClaimTask} refetch={refetch} />
            )}
          </SeriesDailyClaimWrapper>
        </BottomPortal.Above>
      )}
    </Root>
  );
};

CommonPacksPage.fragments = {
  marketRoot: gql`
    fragment CommonPacksPage_marketRoot on MarketRoot {
      id
      tasks(sport: $sport) {
        id
        name
        ...SeriesDailyClaimModule_task
      }
      cardPackGroups(sport: $sport) {
        id
        packConfigGroupType
        cardPacks {
          slug
        }
        ...SeriesCardPackGroup_cardPackGroup
        ...CardPackGroup_cardPackGroup
        ...DailyDeals_cardPackGroup
      }
      gemsPacks: inGameCurrencyPacks(sport: $sport, currency: COMMON_GEM) {
        id
        ...BuyGemsBlock_inGameCurrencyPack
      }
      ...CommonPacksHeader_marketRoot
    }
    ${CommonPacksHeader.fragments.marketRoot}
    ${DailyDeals.fragments.cardPackGroup}
    ${CardPackGroup.fragments.cardPackGroup}
    ${SeriesCardPackGroup.fragments.cardPackGroup}
    ${BuyGemsBlock.fragments.inGameCurrencyPack}
    ${SeriesDailyClaimModule.fragments.task}
  ` as TypedDocumentNode<CommonPacksPage_marketRoot>,
  tokenRoot: gql`
    fragment CommonPacksPage_tokenRoot on TokenRoot {
      purchasableItems(sport: FOOTBALL) {
        slug
        ...BuyGemsBlock_inGameCurrencyPurchasableItem
      }
    }
    ${BuyGemsBlock.fragments.inGameCurrencyPurchasableItem}
  ` as TypedDocumentNode<CommonPacksPage_tokenRoot>,
};
